import React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    CircularProgress,
    Container,
    Paper,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
    clearLedger,
    fetchBranchOptions,
} from '../../store/slices/branchFinanceSlice';
import BranchLedgerTab from './components/BranchLedgerTab';
import BranchCodReceiptsTab from './components/BranchCodReceiptsTab';
import BranchDepositSlipsTab from './components/BranchDepositSlipsTab';
import { BranchOption } from './types/branchFinance.types';

class ErrorBoundary extends React.Component<
    { children: React.ReactNode },
    { hasError: boolean; error: Error | null }
> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error) {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('Branch Finances error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <Alert severity="error">
                    Something went wrong: {this.state.error?.message || 'Unknown error'}
                </Alert>
            );
        }

        return this.props.children;
    }
}

const BranchFinances: React.FC = () => {
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);
    const { branches, branchesLoading, error } = useAppSelector((state) => state.branchFinance);

    const [selectedBranchId, setSelectedBranchId] = React.useState<number | undefined>(undefined);
    const [activeTab, setActiveTab] = React.useState<'cod' | 'deposit' | 'ledger'>('ledger');

    const isManager = user?.type === 'MANAGER';
    const managerBranchId = user?.branch_id;

    React.useEffect(() => {
        dispatch(fetchBranchOptions());
    }, [dispatch]);

    React.useEffect(() => {
        if (isManager && managerBranchId) {
            setSelectedBranchId(managerBranchId);
        }
    }, [isManager, managerBranchId]);

    const branchOptions = React.useMemo(() => {
        if (isManager && managerBranchId) {
            return branches.filter((branch) => branch.id === managerBranchId);
        }
        return branches;
    }, [branches, isManager, managerBranchId]);

    const handleBranchChange = (_: any, newValue: BranchOption | null) => {
        if (newValue) {
            setSelectedBranchId(newValue.id);
        } else {
            setSelectedBranchId(undefined);
        }
        dispatch(clearLedger());
    };

    const handleTabChange = (_: React.SyntheticEvent, newValue: 'cod' | 'deposit' | 'ledger') => {
        setActiveTab(newValue);
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Branch Finances
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Manage COD receipts, deposit slips, and branch ledger activity from a single workspace.
                </Typography>

                {branchesLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress />
                    </Box>
                )}

                <Paper elevation={1} sx={{ borderRadius: 2 }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        aria-label="Branch finance tabs"
                    >
                        <Tab value="ledger" label="Branch Ledger" />
                        <Tab value="cod" label="COD Receipts" />
                        <Tab value="deposit" label="Deposit Slips" />
                    </Tabs>
                </Paper>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {branchOptions.length === 0 && !branchesLoading && (
                <Alert severity="info">No branches available for your account.</Alert>
            )}

            {activeTab === 'ledger' && (
                <Box sx={{ mb: 3, maxWidth: { xs: '100%', md: 420 } }}>
                    <Autocomplete
                        options={branchOptions}
                        getOptionLabel={(option) =>
                            option.city?.name ? `${option.name} (${option.city.name})` : option.name
                        }
                        value={
                            branchOptions.find((branch) => branch.id === selectedBranchId) || null
                        }
                        loading={branchesLoading}
                        onChange={handleBranchChange}
                        disabled={isManager}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Branch"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <React.Fragment>
                                            {branchesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </React.Fragment>
                                    ),
                                }}
                            />
                        )}
                    />
                </Box>
            )}

            <Box sx={{ minHeight: 400 }}>
                {activeTab === 'ledger' && (
                    <>
                {selectedBranchId ? (
                    <BranchLedgerTab selectedBranchId={selectedBranchId} />
                ) : (
                            <Typography color="text.secondary">
                                Please select a branch to view its ledger.
                            </Typography>
                        )}
                    </>
                )}

                {activeTab === 'cod' && (
                    <React.Suspense fallback={<CircularProgress />}>
                        <ErrorBoundary>
                            <BranchCodReceiptsTab />
                        </ErrorBoundary>
                    </React.Suspense>
                )}

                {activeTab === 'deposit' && (
                    <React.Suspense fallback={<CircularProgress />}>
                        <ErrorBoundary>
                            <BranchDepositSlipsTab />
                        </ErrorBoundary>
                    </React.Suspense>
                )}
            </Box>
        </Container>
    );
};

export default BranchFinances;

